// Shared helper to build a human-readable "what the user changed" list for the
// audit trail. Forms across the app resend a fully-recalculated payload on save,
// so the backend can't tell a real edit from a derived/recalculated field. Each
// edit screen computes the real change list here (comparing the originally-loaded
// record with the submitted data) and sends it to the backend as `_auditChanges`.
//
// The backend, when it sees `_auditChanges` on the request body, trusts it verbatim
// and skips its own diff. See auditWorker.js.

export interface AuditChange {
    field: string;
    oldValue: any;
    newValue: any;
}

// Normalize a value for equality comparison: refs by _id, numbers/numeric-strings
// by numeric value, everything else by trimmed string / JSON.
const normVal = (v: any): string => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'object') {
        if ((v as any)._id) return String((v as any)._id);
        try { return JSON.stringify(v); } catch { return String(v); }
    }
    const s = String(v).trim();
    if (/^-?\d+(\.\d+)?$/.test(s)) return String(Number(s)); // "150" === 150
    // Dates in any recognizable format compare by their day (ignore time/format so a
    // re-serialized-but-unchanged date isn't reported as an edit). Use LOCAL date
    // parts (not toISOString) so an ISO "…T00:00:00Z" value and a JS Date.toString()
    // of the same calendar day normalize identically regardless of timezone.
    if (/\d{4}-\d{2}-\d{2}|\w{3}\s+\w{3}\s+\d{1,2}\s+\d{4}/.test(s)) {
        const d = new Date(s);
        if (!isNaN(d.getTime())) {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        }
    }
    return s;
};

// Never audit these (system / derived / noise) fields — matched against the KEY.
// Anchored per-segment so we don't swallow legitimate business keys that merely
// CONTAIN a noise word (e.g. stockLocation, licenseKey, websiteUrl, dispatchDate).
const NOISE_KEY_WORDS = [
    '_id', '__v', 'token', 'jwt', 'etag', 'pdfurl', 'pdflocation', 'signedurl',
    'presigned', 'checksum', 'mimetype', 'filename', 'filepath', 'createdat',
    'updatedat', 'createdby', 'updatedby', 'isdelete', 'isactive', 'password',
    'otp', 'attachment', 'attachments', 'snapshot',
];
const NOISE_KEY = new RegExp(`^(${NOISE_KEY_WORDS.join('|')})$`, 'i');

// Turn a camelCase / snake_case field key into a readable label. e.g.
// "invoiceExpiryDate" -> "Invoice Expiry Date", "po_number" -> "Po Number".
const humanizeKey = (key: string): string => {
    const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_.]/g, ' ').trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * Build the list of user-made changes between two versions of a record.
 *
 * @param original   the record as originally loaded (before edit)
 * @param updated    the record being submitted (after edit)
 * @param fieldLabels map of top-level key -> human label. ONLY these keys are audited.
 * @param opts.arrays optional: audit changes inside named array fields (e.g. line items),
 *                    with per-array item field labels and a function to label each row.
 */
type AuditArrayOpt = {
    arrays?: Array<{
        key: string;                                  // e.g. 'lineItems'
        itemFields: Record<string, string>;           // sub-field key -> label
        rowLabel: (item: any, idx: number) => string; // e.g. item => item.name
    }>;
    // When true, audit EVERY changed top-level field (auto-labelled), not just the
    // ones in `fieldLabels`. `fieldLabels` still supplies nicer labels where present,
    // and `derivedKeys` lists keys to always skip (recalculated totals, etc.).
    auditAll?: boolean;
    derivedKeys?: string[];
};

// Safe public wrapper: audit computation must NEVER break a save. Any unexpected
// data shape just yields an empty change list instead of throwing.
export const buildAuditChanges = (
    original: any,
    updated: any,
    fieldLabels: Record<string, string>,
    opts?: AuditArrayOpt
): AuditChange[] => {
    try {
        return buildAuditChangesImpl(original, updated, fieldLabels, opts);
    } catch (err) {
        // Non-fatal: log and carry on with no audit detail for this save.
        // eslint-disable-next-line no-console
        console.warn('[auditChanges] skipped due to error:', err);
        return [];
    }
};

const buildAuditChangesImpl = (
    original: any,
    updated: any,
    fieldLabels: Record<string, string>,
    opts?: AuditArrayOpt
): AuditChange[] => {
    if (!original || !updated) return [];
    const changes: AuditChange[] = [];

    // Which top-level keys to compare:
    //  - default: only the keys named in `fieldLabels`
    //  - auditAll: every key present in the updated payload (auto-labelled), minus
    //    system/noise keys and the caller's `derivedKeys` (recalculated totals).
    const derived = new Set((opts?.derivedKeys || []).map(k => k.toLowerCase()));
    const arrayKeys = new Set((opts?.arrays || []).map(a => a.key));
    const keysToCheck = opts?.auditAll
        ? Object.keys(updated).filter(k =>
            !k.startsWith('_') &&
            !arrayKeys.has(k) &&                       // arrays handled separately below
            !NOISE_KEY.test(k) &&
            !derived.has(k.toLowerCase()))
        : Object.keys(fieldLabels).filter(k => !NOISE_KEY.test(k));

    for (const key of keysToCheck) {
        const val = updated[key];
        // In auditAll mode, skip nested objects/arrays — only scalar edits are shown
        // cleanly; structured fields would dump JSON. (Named arrays are handled below.)
        if (opts?.auditAll && val !== null && typeof val === 'object' && !Array.isArray(val) && !(val as any)._id) continue;
        if (normVal(original[key]) !== normVal(updated[key])) {
            changes.push({ field: fieldLabels[key] || humanizeKey(key), oldValue: original[key], newValue: updated[key] });
        }
    }

    // Array (e.g. line item) fields.
    for (const arr of opts?.arrays || []) {
        const oldItems: any[] = Array.isArray(original[arr.key]) ? original[arr.key] : [];
        const newItems: any[] = Array.isArray(updated[arr.key]) ? updated[arr.key] : [];

        const oldHasIds = oldItems.some(o => o?._id);
        newItems.forEach((newItem, idx) => {
            const newId = newItem?._id ? String(newItem._id) : '';
            // A new item with no _id (not yet saved) is always an ADD.
            // Otherwise match by _id; if its _id isn't among the old items, it's an ADD.
            let oldItem: any;
            if (!newId) {
                // Unsaved new row. If the old list uses _ids, this can't be an existing
                // row → treat as added. If neither side has ids, fall back to index.
                oldItem = oldHasIds ? undefined : oldItems[idx];
            } else {
                oldItem = oldItems.find(o => o?._id && String(o._id) === newId);
            }
            const label = arr.rowLabel(newItem, idx) || arr.rowLabel(oldItem || {}, idx) || `Item ${idx + 1}`;
            if (!oldItem) {
                changes.push({ field: `${label} added`, oldValue: '', newValue: label });
                return;
            }
            for (const key of Object.keys(arr.itemFields)) {
                if (normVal(oldItem[key]) !== normVal(newItem[key])) {
                    changes.push({
                        field: `${label} — ${arr.itemFields[key]}`,
                        oldValue: oldItem[key],
                        newValue: newItem[key],
                    });
                }
            }
        });

        // Removed rows.
        oldItems.forEach(oldItem => {
            const stillThere = newItems.find(
                n => n?._id && oldItem?._id && String(n._id) === String(oldItem._id)
            );
            if (oldItem?._id && !stillThere) {
                const label = arr.rowLabel(oldItem, -1) || 'Item';
                changes.push({ field: `${label} removed`, oldValue: label, newValue: '' });
            }
        });
    }

    return changes;
};
